import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  CheckCircle,
  CaretRight,
  Confetti,
  MapPin,
  PhoneCall,
  EnvelopeSimple,
  WhatsappLogo,
  Cake,
  Coffee,
  ForkKnife,
} from "@phosphor-icons/react";

export default function Confirmation() {
  const { ref } = useParams();
  const [b, setB] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/bookings/reference/${ref}`).then((r) => setB(r.data)).catch((e) => setErr(e.response?.data?.detail || "Booking not found"));
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [ref]);

  const food = b ? safeJson(b.food_items_json) : [];
  const bevs = b ? safeJson(b.beverage_items_json) : [];
  const venue = cfg?.venue;

  return (
    <div className="min-h-screen bg-mg text-white">
      <Header />
      <div className="pt-28 pb-24 max-w-4xl mx-auto px-6 lg:px-10">
        {err && <div className="text-red-400">{err}</div>}
        {b && (
          <div data-testid="confirmation-card" className="card-lux rounded-lg p-8 sm:p-10">
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center border" style={{ borderColor: "var(--mg-gold)" }}>
                <CheckCircle size={40} weight="duotone" className="text-gold" />
              </div>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl mt-6 text-center">
              <span className="gold-gradient-text">You're booked.</span>
            </h1>
            <p className="mt-3 text-white/70 text-center">The screen is warming up for you.</p>

            <div className="mt-8 divider-lux" />

            {/* Notifications sent */}
            <div className="mt-6 grid sm:grid-cols-3 gap-3 text-xs">
              <NotifPill icon={PhoneCall} label="SMS sent" tone="green" />
              <NotifPill icon={WhatsappLogo} label="WhatsApp sent" tone="green" />
              <NotifPill icon={EnvelopeSimple} label={b.email ? "Email sent" : "Email not sent"} tone={b.email ? "green" : "muted"} />
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-6 text-left text-sm">
              <Field k="Reference" v={b.reference} testid="conf-ref" gold />
              <Field k="Status" v={b.status.toUpperCase()} gold />
              <Field k="Occasion" v={b.occasion} />
              <Field k="Date" v={b.booking_date} />
              <Field k="Slot" v={`${b.slot} (${b.duration_hours}h)`} />
              <Field k="Members" v={String(b.members)} />
              <Field k="Decor" v={b.package_name || "—"} />
              <Field k="Welcome drink" v={b.welcome_drink || "—"} />
              <Field k="Cake" v={b.include_cake ? `1/2 kg cake${b.cake_message ? ` · "${b.cake_message}"` : ""}` : "Not selected"} />
              <Field k="Advance paid" v={`₹${Number(b.advance_amount).toLocaleString("en-IN")} of ₹${Number(b.total_amount).toLocaleString("en-IN")}`} />
              {b.offer_code && <Field k="Offer applied" v={`${b.offer_code} · − ₹${b.discount_amount}`} gold />}
            </div>

            {(food.length > 0 || bevs.length > 0) && (
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {food.length > 0 && (
                  <div className="card-lux rounded-md p-5">
                    <div className="label-tiny flex items-center gap-2"><ForkKnife size={12} /> Food</div>
                    <ul className="mt-3 space-y-1.5 text-sm">
                      {food.map((f) => (
                        <li key={f.id} className="flex justify-between">
                          <span className="text-white/80">{f.name}</span>
                          <span className="text-gold">₹{f.price}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {bevs.length > 0 && (
                  <div className="card-lux rounded-md p-5">
                    <div className="label-tiny flex items-center gap-2"><Coffee size={12} /> Beverages</div>
                    <ul className="mt-3 space-y-1.5 text-sm">
                      {bevs.map((f) => (
                        <li key={f.id} className="flex justify-between">
                          <span className="text-white/80">{f.name}</span>
                          <span className="text-gold">₹{f.price}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Venue */}
            {venue && (
              <div className="mt-8 card-lux rounded-lg p-6" data-testid="venue-card">
                <div className="flex items-start gap-4">
                  <MapPin size={26} className="text-gold shrink-0" weight="duotone" />
                  <div className="flex-1">
                    <div className="font-display text-lg">{venue.name}</div>
                    <div className="text-sm text-white/70 mt-1 leading-relaxed">{venue.address}</div>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <a
                        href={venue.map_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="open-map-btn"
                        className="btn-gold rounded-md px-4 py-2 text-xs inline-flex items-center gap-2"
                      >
                        Open in Maps <CaretRight size={12} weight="bold" />
                      </a>
                      <a
                        href={`tel:${venue.phone.replace(/\s/g, "")}`}
                        className="btn-ghost-gold rounded-md px-4 py-2 text-xs inline-flex items-center gap-2"
                      >
                        <PhoneCall size={12} /> Call venue
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center gap-4 justify-center flex-wrap">
              <Link to="/" className="btn-ghost-gold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2">
                Back to Home <CaretRight size={14} weight="bold" />
              </Link>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`I just booked MoonGlow Screens — reference ${b.reference}! ${venue?.map_url || ""}`)}`}
                target="_blank"
                rel="noreferrer"
                className="btn-gold rounded-md px-5 py-2.5 text-sm inline-flex items-center gap-2"
              >
                <Confetti size={14} weight="duotone" /> Share the news
              </a>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Field({ k, v, gold, testid }) {
  return (
    <div>
      <div className="label-tiny">{k}</div>
      <div data-testid={testid} className={`mt-1 font-display text-lg ${gold ? "gold-gradient-text" : ""}`}>
        {v}
      </div>
    </div>
  );
}

function NotifPill({ icon: Icon, label, tone = "green" }) {
  const color = tone === "green" ? "text-green-400 border-green-500/40" : "text-white/40 border-white/10";
  return (
    <div className={`flex items-center gap-2 rounded-full border ${color} px-4 py-2 justify-center`}>
      <Icon size={14} weight="duotone" />
      <span className="text-xs">{label}</span>
    </div>
  );
}

function safeJson(s) {
  try { return JSON.parse(s || "[]"); } catch { return []; }
}
